import { Form } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import WizrFlexLayout from '../WizrFlexLayout'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import WizrLoader from '../WizrLoader'
import { getTenantId } from '../../utils'
import {
  useGetTenantDetails,
  useListProductsSelectors,
  useListTenantSelector,
} from '../../redux/selectors/content'
import { dispatchListTenants, dispatchProuctsList } from '../../redux/actions/content'

import {
  type AiState,
  type DbState,
  type LlmParam,
  type LlmParamErrors,
  INITIAL_AI_STATE,
  INITIAL_DB_STATE,
} from './createLlmDb/types'
import { BASIC_TENANT_ID } from './createLlmDb/constants'
import { ToolFormCard } from './createLlmDb/styles'
import { buildAiEditHydrationKey, hydrateAiStateFromRecord } from './createLlmDb/hydration'
import LlmDbCoreFields from './createLlmDb/LlmDbCoreFields'
import LlmDbTenantSection from './createLlmDb/LlmDbTenantSection'
import LlmParametersList from './createLlmDb/LlmParametersList'
import DbFormFields from './createLlmDb/DbFormFields'

export type { CreateLlmDbAiPayload, CreateLlmDbDbPayload, CreateLlmDbFormProps } from './createLlmDb/types'
export { isCreateLlmDbAiPayload, buildLlmConfigApiPayload } from './createLlmDb/mappers'

import type { CreateLlmDbFormProps } from './createLlmDb/types'

const isBlank = (text?: string) => !text || text.trim() === ''

const scrollToField = (fieldKey: string) => {
  setTimeout(() => {
    document
      .querySelector(`[data-validation-field="${fieldKey}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, 100)
}

const CreateLlmDbForm = ({
  variant,
  onCancel,
  onSave,
  initialRecord,
}: CreateLlmDbFormProps): JSX.Element => {
  const products = useListProductsSelectors()
  const tenantDetails = useGetTenantDetails()
  const tenantList = useListTenantSelector()
  const isMasterTenant = Boolean(tenantDetails?.master_tenant)
  const loggedTenantId = String(getTenantId() ?? '').trim()

  const existingRecordId =
    initialRecord?.id != null && String(initialRecord.id).trim() !== ''
      ? String(initialRecord.id)
      : undefined

  const [aiState, setAiState] = useState<AiState>(INITIAL_AI_STATE)
  const [dbState, setDbState] = useState<DbState>(INITIAL_DB_STATE)
  const [llmParams, setLlmParams] = useState<LlmParam[]>([{ id: 'p1', name: '', value: '' }])
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([])
  const [tenantScopeSelected, setTenantScopeSelected] = useState(false)
  const [dbTenantScopeSelected, setDbTenantScopeSelected] = useState(false)

  const [aiErrors, setAiErrors] = useState<Partial<Record<string, boolean>>>({})
  const [llmParamErrors, setLlmParamErrors] = useState<LlmParamErrors>({})
  const [dbErrors, setDbErrors] = useState<Partial<Record<string, boolean>>>({})
  const [productListPending, setProductListPending] = useState(false)
  const [tenantListPending, setTenantListPending] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const aiEditHydrationKey = useMemo(
    () => buildAiEditHydrationKey(variant, initialRecord ?? null),
    [variant, initialRecord],
  )

  // Flag scope as selected on edit mode
  useEffect(() => {
    if (existingRecordId) {
      setTenantScopeSelected(true)
      setDbTenantScopeSelected(true)
    }
  }, [existingRecordId])

  // Load product list
  useEffect(() => {
    if (variant !== 'ai' && variant !== 'db') { setProductListPending(false); return }
    setProductListPending(true)
    void (dispatchProuctsList({ skipContentLoading: true }) as Promise<unknown>).finally(() => {
      setProductListPending(false)
    })
  }, [variant, aiEditHydrationKey])

  // Load tenant list (master tenant only)
  useEffect(() => {
    if (variant !== 'ai' || !isMasterTenant) { setTenantListPending(false); return }
    setTenantListPending(true)
    void (dispatchListTenants({ skipContentLoading: true }) as Promise<unknown>).finally(() => {
      setTenantListPending(false)
    })
  }, [variant, aiEditHydrationKey, isMasterTenant])

  // Hydrate form state from initialRecord on edit
  useEffect(() => {
    if (variant !== 'ai' || !initialRecord) return
    const { aiState: hydrated, llmParams: hydratedParams, selectedTenantIds: hydratedIds } =
      hydrateAiStateFromRecord(initialRecord, isMasterTenant)
    setAiState(hydrated)
    setLlmParams(hydratedParams)
    setSelectedTenantIds(
      isMasterTenant ? hydratedIds : loggedTenantId ? [loggedTenantId] : [],
    )
  }, [variant, isMasterTenant, loggedTenantId, aiEditHydrationKey])

  const productOptions = useMemo(
    () =>
      (products ?? [])
        .map((item: any) => ({
          label: item?.product_name,
          value: item?.id != null && item?.id !== '' ? String(item.id) : '',
        }))
        .filter((o: { value: string }) => o.value),
    [products],
  )

  const tenantListOptions = useMemo(
    () =>
      (tenantList ?? [])
        .map((item: any) => {
          const id = item?.tenant_id ?? item?.tenantId
          return {
            label: item?.tenant_name != null ? String(item.tenant_name) : '',
            value: id != null && id !== '' ? String(id) : '',
          }
        })
        .filter((o: { value: string }) => o.value),
    [tenantList],
  )

  // ─── Field helpers ────────────────────────────────────────────────────────
  const setAiField = (key: keyof AiState, value: string) =>
    setAiState((s) => ({ ...s, [key]: value }))

  const setDbField = (key: keyof DbState, value: string) =>
    setDbState((s) => ({ ...s, [key]: value }))

  const clearAiErrorKey = (key: string) =>
    setAiErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })

  const clearDbErrorKey = (key: string) =>
    setDbErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })

  // ─── LLM param handlers ───────────────────────────────────────────────────
  const addParamRow = () =>
    setLlmParams((rows) => [...rows, { id: `p-${Date.now()}`, name: '', value: '' }])

  const removeParamRow = (id: string) => {
    setLlmParams((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.id !== id)))
    setLlmParamErrors((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const updateParamRow = (id: string, field: 'name' | 'value', value: string) => {
    setLlmParams((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
    if (value.trim()) {
      setLlmParamErrors((prev) => {
        const row = prev[id]
        if (!row) return prev
        const nextRow = { ...row }
        if (field === 'name') delete nextRow.name
        else delete nextRow.value
        if (!nextRow.name && !nextRow.value) {
          const rest = { ...prev }
          delete rest[id]
          return rest
        }
        return { ...prev, [id]: nextRow }
      })
    }
  }

  // ─── Validation ───────────────────────────────────────────────────────────
  const validateAiFields = (): boolean => {
    const next: Partial<Record<string, boolean>> = {}
    const nextLlm: LlmParamErrors = {}
    let firstField: string | null = null

    const mark = (key: string) => { next[key] = true; if (!firstField) firstField = key }

    if (isBlank(aiState.displayName)) mark('displayName')
    if (isBlank(aiState.systemVersion)) mark('systemVersion')
    if (isBlank(aiState.status)) mark('status')
    if (isBlank(aiState.modelType)) mark('modelType')
    if (isBlank(aiState.llmType)) mark('llmType')
    if (isBlank(aiState.deploymentName)) mark('deploymentName')
    if (isBlank(aiState.modelName)) mark('modelName')
    if (isBlank(aiState.apiType)) mark('apiType')
    if (isBlank(aiState.apiBaseUrl)) mark('apiBaseUrl')
    if (isBlank(aiState.apiVersion)) mark('apiVersion')
    if (isBlank(aiState.apiSecretKey)) mark('apiSecretKey')
    if (!existingRecordId && isBlank(aiState.apiSecretValue)) mark('apiSecretValue')
    if (isBlank(aiState.functionCallingEnabled)) mark('functionCallingEnabled')

    if (isMasterTenant) {
      if (!tenantScopeSelected) mark('tenantScope')
      if (tenantScopeSelected && aiState.tenantScope === 'specific' && selectedTenantIds.length === 0) {
        mark('tenantMulti')
      }
    }

    llmParams.forEach((p) => {
      const nameEmpty = isBlank(p.name)
      const valueEmpty = isBlank(p.value)
      if (nameEmpty && valueEmpty) return
      if (nameEmpty || valueEmpty) {
        nextLlm[p.id] = { name: nameEmpty, value: valueEmpty }
        if (!firstField) firstField = `llm-${p.id}`
      }
    })

    setAiErrors(next)
    setLlmParamErrors(nextLlm)
    if (!firstField) return true
    scrollToField(firstField)
    return false
  }

  const validateDbFields = (): boolean => {
    const next: Partial<Record<string, boolean>> = {}
    let firstField: string | null = null
    const mark = (key: string) => { next[key] = true; if (!firstField) firstField = key }

    if (isBlank(dbState.displayName)) mark('db-displayName')
    if (isBlank(dbState.toolKey)) mark('db-toolKey')
    if (isBlank(dbState.connectionHost)) mark('db-connectionHost')
    if (isBlank(dbState.port)) mark('db-port')
    if (isBlank(dbState.databaseName)) mark('db-databaseName')
    if (isBlank(dbState.status)) mark('db-status')
    if (!dbTenantScopeSelected) mark('db-tenantScope')
    if (isBlank(dbState.notes)) mark('db-notes')

    setDbErrors(next)
    if (!firstField) return true
    scrollToField(firstField)
    return false
  }

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (isSaving) return
    if (variant === 'ai') {
      if (!validateAiFields()) return
      const tenantScope = isMasterTenant ? aiState.tenantScope : 'specific'
      const tenantIds = !isMasterTenant
        ? (loggedTenantId ? [loggedTenantId] : [])
        : tenantScope === BASIC_TENANT_ID
          ? ['basic']
          : existingRecordId
            ? (selectedTenantIds.length ? [selectedTenantIds[0]] : [])
            : selectedTenantIds
      setIsSaving(true)
      try {
        await Promise.resolve(
          onSave?.({
            ...aiState,
            tenantScope,
            tenantIds,
            loggedTenantId,
            isTenantAdmin: isMasterTenant,
            ...(existingRecordId ? { id: existingRecordId } : {}),
            llmParameters: llmParams
              .filter((p) => !isBlank(p.name) && !isBlank(p.value))
              .map(({ name, value }) => ({ name, value })),
          }),
        )
      } finally {
        setIsSaving(false)
      }
    } else {
      if (!validateDbFields()) return
      setIsSaving(true)
      try {
        await Promise.resolve(onSave?.({ ...dbState }))
      } finally {
        setIsSaving(false)
      }
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <ToolFormCard flexDirection='column' alignItems='stretch' justifyContent='flex-start'>
      {isSaving ? <WizrLoader loading fullPage={false} useDataAwareLoading={false} /> : null}
      <Form
        style={{ width: '100%', pointerEvents: isSaving ? 'none' : undefined }}
        onFinish={handleSave}
        autoComplete='off'
      >
        <WizrFlexLayout
          flexDirection='column'
          background='transparent'
          alignItems='stretch'
          style={{ width: '100%', gap: '16px' }}
        >
          {variant === 'ai' ? (
            <>
              <LlmDbCoreFields
                aiState={aiState}
                setAiField={setAiField}
                aiErrors={aiErrors}
                clearAiErrorKey={clearAiErrorKey}
                existingRecordId={existingRecordId}
              />
              <LlmDbTenantSection
                aiState={aiState}
                setAiField={setAiField}
                aiErrors={aiErrors}
                clearAiErrorKey={clearAiErrorKey}
                isMasterTenant={isMasterTenant}
                existingRecordId={existingRecordId}
                tenantScopeSelected={tenantScopeSelected}
                setTenantScopeSelected={setTenantScopeSelected}
                selectedTenantIds={selectedTenantIds}
                setSelectedTenantIds={setSelectedTenantIds}
                tenantListPending={tenantListPending}
                tenantListOptions={tenantListOptions}
                productOptions={productOptions}
                productListPending={productListPending}
              />
              <LlmParametersList
                llmParams={llmParams}
                llmParamErrors={llmParamErrors}
                addParamRow={addParamRow}
                removeParamRow={removeParamRow}
                updateParamRow={updateParamRow}
              />
            </>
          ) : (
            <DbFormFields
              dbState={dbState}
              setDbField={setDbField}
              dbErrors={dbErrors}
              clearDbErrorKey={clearDbErrorKey}
              dbTenantScopeSelected={dbTenantScopeSelected}
              setDbTenantScopeSelected={setDbTenantScopeSelected}
              productOptions={productOptions}
              productListPending={productListPending}
            />
          )}
        </WizrFlexLayout>

        <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
          <WizrFlexSpacing justifyContent='flex-end' flexDirection='row' size='large' background='#FFF'>
            <WizrButton
              type='default'
              text='Cancel'
              onClick={onCancel}
              disabled={isSaving}
              style={{ borderRadius: '20px' }}
            />
            <WizrButton
              htmlType='submit'
              text='Save'
              disabled={isSaving}
              style={{ borderRadius: '20px' }}
            />
          </WizrFlexSpacing>
        </Form.Item>
      </Form>
    </ToolFormCard>
  )
}

export default CreateLlmDbForm
